/**
 * KAN-651: блок "A/B первых сообщений".
 *
 * Варианты-брифы оператора (добавить, править, генерировать пул текстов) и таблица
 * статистики A/B (варианты-брифы и шаблоны кабинета).
 *
 * KAN-752: блок живёт на странице оператора (/users/:id?tab=greetings) и всегда
 * работает в контексте одного оператора: список и статистика по operator_id,
 * новый вариант создаётся для этого оператора. Варианты организации другим
 * операторам больше не достаются.
 */
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGreetingVariantMutations, useGreetingVariants } from '@/hooks/useGreetingVariants';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { GreetingAbTable } from './GreetingAbTable';
import { GreetingVariantCard } from './GreetingVariantCard';

export const GREETING_BLOCK_HELP =
  'Бриф - короткое задание для ИИ: о чём и каким тоном писать первое сообщение. По брифу генерируется пул текстов; при рассылке включённые варианты чередуются поровну, внутри варианта уходит наименее отправленный текст. Нет включённых вариантов - используются шаблоны из настроек канала.';

interface GreetingVariantsBlockProps {
  /** Оператор, чьи варианты показываются и создаются. */
  operatorId: string;
}

export function GreetingVariantsBlock({ operatorId }: GreetingVariantsBlockProps): JSX.Element {
  const { data } = useGreetingVariants(operatorId);
  const { create } = useGreetingVariantMutations(operatorId);
  const variants = data ?? [];

  return (
    <section data-testid="greeting-variants-block" className="mt-6">
      <div className="mb-1 flex items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">A/B первых сообщений</h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-testid="greeting-variant-add"
          disabled={create.isPending}
          onClick={() => create.mutate('')}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Добавить вариант
        </Button>
      </div>
      <p className="mb-3 text-xs text-gray-500" data-testid="greeting-variants-help">
        {GREETING_BLOCK_HELP}
      </p>
      {create.isError && (
        <p className="mb-2 text-xs text-red-700">
          {extractErrorMessage(create.error, 'Не удалось добавить вариант')}
        </p>
      )}
      <div className="flex flex-col gap-3">
        {variants.map((variant) => (
          <GreetingVariantCard key={variant.id} variant={variant} />
        ))}
      </div>
      <GreetingAbTable operatorId={operatorId} />
    </section>
  );
}

export default GreetingVariantsBlock;
